import React, { useCallback } from "react";

import { useAppData } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";

import { useEntityDetail } from "./entity-detail-provider";
import { IssueTypeDetailDialog } from "./issue-type-detail-dialog";
import { MemberProfileDialog } from "./member-profile-dialog";
import { StageDetailDialog } from "./stage-detail-dialog";
import { TeamProfileDialog } from "./team-profile-dialog";
import { WorkflowDetailDialog } from "./workflow-detail-dialog";

// Singleton dialog host — mounted ONCE in the workspace layout route, below
// the provider that owns the state (split-context rule), never per chip.
// `issue` never reaches here (the provider delegates it to the issue-view
// overlay).
export function EntityDetailDialogs() {
  const { active, closeEntity } = useEntityDetail();
  const { currentUser } = useAppData();
  const { push } = useAppNavigate();
  const canManageTeams = usePermission("manage.teams");
  const canManageUsers = usePermission("manage.users");

  const onOpenChange = (open: boolean) => {
    if (!open) closeEntity();
  };

  const handleEditProfile = useCallback(
    (userId: string) => {
      if (userId === currentUser?.userId) {
        push("/settings/profile");
      } else {
        push(`/settings/members/${userId}`);
      }
    },
    [push, currentUser?.userId],
  );

  const handleEditTeam = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}`);
    },
    [push],
  );

  // Membership-only screen for team leads who lack `manage.teams`; routes to
  // the team's members page, which the backend permits leads to use. The
  // dialog decides whether to show it (lead + no manage.teams).
  const handleManageMembers = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}/members`);
    },
    [push],
  );

  switch (active?.kind) {
    case "user":
      return (
        <MemberProfileDialog
          userId={active.id}
          open
          onOpenChange={onOpenChange}
          onEditProfile={
            // Self-edit routes to /settings/profile (always allowed); editing
            // anyone else lands on the gated settings/members screen.
            canManageUsers || active.id === currentUser?.userId
              ? handleEditProfile
              : undefined
          }
        />
      );
    case "team":
      return (
        <TeamProfileDialog
          teamId={active.id}
          open
          onOpenChange={onOpenChange}
          onEditTeam={canManageTeams ? handleEditTeam : undefined}
          onManageMembers={handleManageMembers}
        />
      );
    case "stage":
      return <StageDetailDialog stageId={active.id} open onOpenChange={onOpenChange} />;
    case "workflow":
      return (
        <WorkflowDetailDialog workflowId={active.id} open onOpenChange={onOpenChange} />
      );
    case "issueType":
      return (
        <IssueTypeDetailDialog
          issueTypeId={active.id}
          open
          onOpenChange={onOpenChange}
        />
      );
    default:
      return null;
  }
}
